import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { transactionTemplatesService } from '../services/transactionTemplates.service';
import { partyRoleLabel } from '../utils/transactionTemplates';
import ConfirmDialog from '../components/common/ConfirmDialog';
// Same Transaction detail layout (sidebar + main area) as the template detail page.
import './TransactionDetailPage.css';
import './TemplateDetailPage.css';
import './TemplateEditorPage.css';

const STATES = [{ value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' }];
const SIDES = [{ value: 'LISTING', label: 'Listing' }, { value: 'BUYING', label: 'Buying' }];
const SIDE_PARTY = { LISTING: 'SELLER', BUYING: 'BUYER' };
const PARTY_ROLES = [
  'TRANSACTION_COORDINATOR', 'ESCROW_OFFICER', 'TITLE_OFFICER', 'LENDER', 'INSPECTOR',
  'APPRAISER', 'ATTORNEY', 'INSURANCE_AGENT', 'HOME_WARRANTY', 'PHOTOGRAPHER', 'STAGER',
];

let tempKey = 0;
const nextKey = (prefix) => `new-${prefix}-${++tempKey}`;

const emptyTemplate = () => ({
  name: '',
  state: 'CA',
  side: 'LISTING',
  folders: [{ slug: '', name: 'Disclosures', _key: nextKey('folder') }],
  items: [],
  parties: [],
});

// Editor rows need a stable React key and a folder reference that survives a rename,
// so folders carry a client-only _key and items point at it until save.
const toEditable = (t) => {
  const folders = t.folders.map((f) => ({ ...f, _key: f.slug }));
  return {
    ...t,
    folders,
    items: t.items.map((i) => ({ ...i, _key: i.key, _folderKey: i.folder })),
  };
};

const toPayload = (t) => {
  const slugByKey = {};
  t.folders.forEach((f) => { slugByKey[f._key] = f.slug || ''; });
  return {
    name: t.name,
    state: t.state,
    side: t.side,
    folders: t.folders.map(({ slug, name }) => ({ slug: slug || '', name })),
    // A new folder has no slug until the backend makes one from its name, so new items
    // name their folder by its position instead and the slug is filled in below.
    items: t.items.map((i) => ({
      key: i.key || '',
      name: i.name,
      folder: slugByKey[i._folderKey] || `__folder${t.folders.findIndex((f) => f._key === i._folderKey)}`,
      mandatory: !!i.mandatory,
      type: i.type,
      form: i.type === 'FORM' ? i.form : null,
    })),
    parties: t.parties,
  };
};

/** Searchable picker over the state forms library. */
const FormPicker = ({ library, value, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (q ? library.filter((f) => f.code.toLowerCase().includes(q) || f.name.toLowerCase().includes(q)) : library).slice(0, 50);
  }, [library, query]);
  const selected = value && library.find((f) => f.code === value.code);

  return (
    <div className="tpl-form-picker">
      <input
        type="text"
        className="tpl-input"
        placeholder={library.length ? 'Search forms by code or name…' : 'Forms library not available'}
        value={open ? query : (selected ? `${selected.code} — ${selected.name}` : (value?.code || ''))}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled || library.length === 0}
      />
      {open && matches.length > 0 && (
        <ul className="tpl-form-picker-list" role="listbox">
          {matches.map((f) => (
            <li key={f.code}>
              <button type="button" onMouseDown={() => { onChange(f.form); setOpen(false); }}>
                <strong>{f.code}</strong> <span>{f.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TemplateEditorPage = () => {
  const { templateId } = useParams();
  const isEdit = !!templateId;
  const navigate = useNavigate();
  const [template, setTemplate] = useState(isEdit ? null : emptyTemplate());
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('documents');
  const [library, setLibrary] = useState([]);
  const [libraryError, setLibraryError] = useState('');
  const [saveError, setSaveError] = useState('');
  // Folder waiting for "Remove folder?" confirmation (only when it holds documents) — same as mobile.
  const [pendingFolderRemove, setPendingFolderRemove] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    transactionTemplatesService.getTemplate(templateId)
      .then((t) => {
        if (!t.ownerUserId) { setLoadError("Rialty templates can't be edited."); return; }
        setTemplate(toEditable(t));
      })
      .catch(() => setLoadError("This template couldn't be found."));
  }, [isEdit, templateId]);

  const state = template?.state;
  useEffect(() => {
    if (!state) return;
    let cancelled = false;
    setLibraryError('');
    transactionTemplatesService.getFormLibrary(state)
      .then((forms) => { if (!cancelled) setLibrary(forms); })
      .catch((e) => {
        if (cancelled) return;
        setLibrary([]);
        setLibraryError(e.response?.status === 409
          ? 'Connect your Lone Wolf account to pick California forms. Upload items still work.'
          : "Couldn't load the forms library. Upload items still work.");
      });
    return () => { cancelled = true; };
  }, [state]);

  const update = (changes) => setTemplate((prev) => ({ ...prev, ...changes }));
  const updateItem = (key, changes) => update({ items: template.items.map((i) => (i._key === key ? { ...i, ...changes } : i)) });
  const updateFolder = (key, changes) => update({ folders: template.folders.map((f) => (f._key === key ? { ...f, ...changes } : f)) });

  const addFolder = () => update({ folders: [...template.folders, { slug: '', name: '', _key: nextKey('folder') }] });
  const doRemoveFolder = (key) => update({
    folders: template.folders.filter((f) => f._key !== key),
    items: template.items.filter((i) => i._folderKey !== key),
  });
  const removeFolder = (key) => {
    if (template.items.some((i) => i._folderKey === key)) setPendingFolderRemove(key);
    else doRemoveFolder(key);
  };
  const addItem = (folderKey) => update({
    items: [...template.items, { key: '', name: '', type: 'FORM', form: null, mandatory: false, _key: nextKey('item'), _folderKey: folderKey }],
  });
  const toggleParty = (role) => update({
    parties: template.parties.includes(role) ? template.parties.filter((r) => r !== role) : [...template.parties, role],
  });

  const handleSave = async () => {
    setSaveError('');
    if (!template.name.trim()) { setSaveError('Give the template a name.'); return; }
    if (template.folders.some((f) => !f.name.trim())) { setSaveError('Every folder needs a name.'); return; }
    if (template.items.some((i) => !i.name.trim())) { setSaveError('Every document needs a name.'); return; }
    if (template.items.some((i) => i.type === 'FORM' && !i.form)) { setSaveError('Pick a form for every Form document, or make it an Upload.'); return; }

    setIsSaving(true);
    try {
      const payload = toPayload(template);
      // New folders get their slug from the backend; send them slug-less and resolve the
      // positional placeholders to the slug the backend will derive from the name.
      payload.items = payload.items.map((i) => {
        const m = /^__folder(\d+)$/.exec(i.folder);
        if (!m) return i;
        const name = payload.folders[Number(m[1])]?.name || '';
        return { ...i, folder: slugFor(name, payload.folders, Number(m[1])) };
      });
      payload.folders = payload.folders.map((f, idx) => ({ ...f, slug: f.slug || slugFor(f.name, payload.folders, idx) }));
      const saved = isEdit
        ? await transactionTemplatesService.updatePersonalTemplate(templateId, payload)
        : await transactionTemplatesService.createPersonalTemplate(payload);
      navigate(`/templates/${saved.id}`);
    } catch (e) {
      setSaveError(e.response?.data?.message || "Couldn't save the template. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loadError || !template) {
    return (
      <div className="txn-detail-container">
        <div className="txn-layout-wrapper"><div className="txn-layout-container"><div className="txn-main-area">
          <div className="tpl-detail-message">
            {loadError || 'Loading template…'}
            {loadError && (
              <button className="txn-back-button" onClick={() => navigate('/transactions?view=templates')} style={{ marginTop: '20px' }}>
                ← Back to Templates
              </button>
            )}
          </div>
        </div></div></div>
      </div>
    );
  }

  const partyOptions = [SIDE_PARTY[template.side], ...PARTY_ROLES];
  const documentCount = template.items.length;

  return (
    <div className="txn-detail-container">
      <div className="txn-layout-wrapper">
      <div className="txn-layout-container">
        <div className="txn-sidebar">
          <div className="txn-sidebar-header">
            <button className="txn-back-button-small" onClick={() => navigate(isEdit ? `/templates/${templateId}` : '/transactions?view=templates')}>
              <span>←</span>
              <span>Back</span>
            </button>
            <div className="txn-sidebar-title">{isEdit ? 'Edit template' : 'New template'}</div>

            <label className="tpl-field">
              <span className="tpl-field-label">Template name</span>
              <input className="tpl-input" value={template.name} maxLength={80}
                placeholder="e.g. My Luxury Listing" onChange={(e) => update({ name: e.target.value })} />
            </label>
            <div className="tpl-field-row">
              <label className="tpl-field">
                <span className="tpl-field-label">State</span>
                <select className="tpl-input" value={template.state} disabled={isEdit}
                  onChange={(e) => update({ state: e.target.value, items: template.items.map((i) => ({ ...i, form: null })) })}>
                  {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label className="tpl-field">
                <span className="tpl-field-label">Side</span>
                <select className="tpl-input" value={template.side} disabled={isEdit}
                  onChange={(e) => update({ side: e.target.value, parties: template.parties.filter((r) => r !== SIDE_PARTY[template.side]) })}>
                  {SIDES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
            </div>
            {isEdit && <span className="tpl-field-hint">State and side can't change after the template is created.</span>}
          </div>

          <div className="txn-sidebar-nav">
            {[
              { id: 'documents', label: 'Documents', count: documentCount },
              { id: 'parties', label: 'Parties', count: template.parties.length },
            ].map((tab) => (
              <div key={tab.id} className={`txn-sidebar-nav-item ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                <div className="txn-sidebar-nav-label"><span>{tab.label}</span></div>
                <div className="txn-sidebar-nav-count">{tab.count}</div>
              </div>
            ))}
          </div>

          <div className="tpl-editor-actions">
            {saveError && <p className="tpl-editor-error">{saveError}</p>}
            <button type="button" className="tpl-editor-save" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
            </button>
          </div>
        </div>

        <div className="txn-main-area">
          <div className="tpl-detail-panel">
            {activeTab === 'documents' && (
              <>
                <div className="tpl-editor-head">
                  <div>
                    <h2 className="tpl-detail-title">Documents</h2>
                    <p className="tpl-detail-subtitle">Folders and the documents each one needs. Mandatory documents need a reason to remove on a transaction.</p>
                  </div>
                  <button type="button" className="tpl-editor-add" onClick={addFolder}>+ Add folder</button>
                </div>
                {libraryError && <p className="tpl-editor-warning">{libraryError}</p>}

                {template.folders.map((folder) => {
                  const items = template.items.filter((i) => i._folderKey === folder._key);
                  return (
                    <section key={folder._key} className="tpl-editor-folder">
                      <div className="tpl-editor-folder-head">
                        <input className="tpl-input tpl-input--folder" value={folder.name} placeholder="Folder name"
                          onChange={(e) => updateFolder(folder._key, { name: e.target.value })} />
                        <button type="button" className="tpl-editor-icon-btn" onClick={() => removeFolder(folder._key)}
                          title="Delete the folder and its documents">
                          Delete folder
                        </button>
                      </div>
                      {items.length === 0 && <p className="tpl-editor-empty">No documents in this folder yet.</p>}
                      {items.map((item) => (
                        <div key={item._key} className="tpl-editor-item">
                          <input className="tpl-input tpl-editor-item-name" value={item.name} placeholder="Document name"
                            onChange={(e) => updateItem(item._key, { name: e.target.value })} />
                          <select className="tpl-input tpl-editor-item-type" value={item.type}
                            onChange={(e) => updateItem(item._key, { type: e.target.value, form: null })}>
                            <option value="FORM">Form</option>
                            <option value="UPLOAD">Upload (agent adds the file)</option>
                          </select>
                          {item.type === 'FORM' && (
                            <FormPicker library={library} value={item.form}
                              onChange={(form) => {
                                const picked = library.find((f) => f.code === form.code);
                                updateItem(item._key, { form, name: item.name || picked?.name || form.code });
                              }} />
                          )}
                          <label className="tpl-editor-mandatory">
                            <input type="checkbox" checked={!!item.mandatory}
                              onChange={(e) => updateItem(item._key, { mandatory: e.target.checked })} />
                            Mandatory
                          </label>
                          <button type="button" className="tpl-editor-icon-btn" title="Remove"
                            onClick={() => update({ items: template.items.filter((i) => i._key !== item._key) })}>
                            Remove
                          </button>
                        </div>
                      ))}
                      <button type="button" className="tpl-editor-link" onClick={() => addItem(folder._key)}>+ Add document</button>
                    </section>
                  );
                })}
              </>
            )}

            {activeTab === 'parties' && (
              <>
                <h2 className="tpl-detail-title">Parties</h2>
                <p className="tpl-detail-subtitle">Roles added to the transaction as "To be invited". Nobody is invited automatically.</p>
                <div className="tpl-editor-roles">
                  {partyOptions.map((role) => (
                    <label key={role} className={`tpl-editor-role${template.parties.includes(role) ? ' is-on' : ''}`}>
                      <input type="checkbox" checked={template.parties.includes(role)} onChange={() => toggleParty(role)} />
                      {partyRoleLabel(role)}
                    </label>
                  ))}
                </div>
              </>
            )}

          </div>
        </div>
      </div>
      </div>
      {pendingFolderRemove && (
        <ConfirmDialog
          title="Remove folder?"
          body="Its documents are removed from the template too."
          confirmLabel="Remove"
          danger
          onConfirm={() => { doRemoveFolder(pendingFolderRemove); setPendingFolderRemove(null); }}
          onCancel={() => setPendingFolderRemove(null)}
        />
      )}
    </div>
  );
};

// Mirrors the backend's slug rule (PersonalTemplateService.uniqueSlug): lowercase,
// non-alphanumerics → "-", "documents" reserved, duplicates get -2/-3, and a
// "Disclosures" folder maps to the existing disclosure folder.
function slugFor(name, folders, index) {
  const taken = new Set(['documents']);
  for (let i = 0; i < index; i += 1) {
    const f = folders[i];
    taken.add(f.slug || slugFor(f.name, folders, i));
  }
  let base = (name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'item';
  if (base.length > 36) base = base.slice(0, 36).replace(/-+$/, '');
  if (base === 'disclosures' && !taken.has('disclosure')) return 'disclosure';
  let slug = base;
  for (let n = 2; taken.has(slug); n += 1) slug = `${base}-${n}`;
  return slug;
}

export default TemplateEditorPage;
